import { Children, type FC, Fragment, isValidElement, useCallback, useEffect, useId, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import ReactDOM from 'react-dom';

import type { IUseAppModal } from '../../../hooks/modal';
import { CloseSVG } from '../svgs';

type OpacityNumberType =
	| 0.1
	| 0.15
	| 0.2
	| 0.25
	| 0.3
	| 0.35
	| 0.4
	| 0.45
	| 0.5
	| 0.55
	| 0.6
	| 0.65
	| 0.7
	| 0.75
	| 0.8
	| 0.85
	| 0.9
	| 0.95
	| 1
	| 0;

export interface IAppModal {
	modalProps: IUseAppModal;
	children?: any;
	size?: 'wide' | 'medium' | 'small';
	disableClose?: boolean;
	opacityNumber?: OpacityNumberType;
	ariaDescribedBy?: string;
	dialogClassName?: string;
	dialogStyle?: React.CSSProperties;
	bodyClassName?: string;
	bodyStyle?: React.CSSProperties;
	/**
	 * Overrides `modalProps.title`, for a dialog whose title changes per step. Omit it and the title
	 * set by `handleOpenModal` is shown, as before.
	 */
	title?: React.ReactNode;
	/**
	 * Where focus lands when the modal opens. Called once, after the open animation's first frame;
	 * returning null falls back to the first focusable element (the close button, usually).
	 */
	initialFocus?: () => HTMLElement | null | undefined;
}

// Get all focusable elements within a container
const getFocusableElements = (container: HTMLElement): HTMLElement[] => {
	const focusableSelectors = [
		'button:not([disabled])',
		'[href]',
		'input:not([disabled])',
		'select:not([disabled])',
		'textarea:not([disabled])',
		'[tabindex]:not([tabindex="-1"])',
	].join(', ');

	return Array.from(container.querySelectorAll<HTMLElement>(focusableSelectors));
};

const AppModal: FC<IAppModal> = ({
	modalProps,
	size,
	children,
	disableClose,
	opacityNumber,
	ariaDescribedBy,
	dialogClassName,
	dialogStyle,
	bodyClassName,
	bodyStyle,
	title: titleOverride,
	initialFocus,
}) => {
	const { openModal, title: modalTitle, closeRef, handleCloseModal } = modalProps;
	const title = titleOverride ?? modalTitle;
	const modalRef = useRef<HTMLDivElement>(null);
	const previousFocusRef = useRef<HTMLElement | null>(null);
	const titleId = useId();
	const descriptionId = ariaDescribedBy || `${titleId}-description`;

	// Only a component child can take `modalProps`. On a DOM element (or a Fragment) React logs
	// "does not recognize the `modalProps` prop on a DOM element" — and modals with a `<div>` child did.
	const matchChild: any = Children.map(children, (child) => {
		if (isValidElement(child) && typeof child.type !== 'string' && child.type !== Fragment)
			return { ...child, props: { ...(child.props as object), modalProps } };
		return child;
	});

	// Handle keyboard navigation and escape key
	const handleKeyDown = useCallback(
		(event: KeyboardEvent) => {
			if (!openModal) return;

			// Close on Escape key
			if (event.key === 'Escape' && !disableClose) {
				event.preventDefault();
				handleCloseModal();
				return;
			}

			// Trap focus within modal
			if (event.key === 'Tab' && modalRef.current) {
				const focusableElements = getFocusableElements(modalRef.current);
				if (focusableElements.length === 0) return;

				const firstElement = focusableElements[0];
				const lastElement = focusableElements[focusableElements.length - 1];

				if (event.shiftKey) {
					// Shift + Tab: move focus backward
					if (document.activeElement === firstElement) {
						event.preventDefault();
						lastElement.focus();
					}
				} else {
					// Tab: move focus forward
					if (document.activeElement === lastElement) {
						event.preventDefault();
						firstElement.focus();
					}
				}
			}
		},
		[openModal, disableClose, handleCloseModal]
	);

	/**
	 * Always-current handler behind a stable identity.
	 *
	 * `useModal()` returns fresh handler functions on every render, so `handleKeyDown` changes on
	 * every parent re-render. Listing it in the effect below made the effect re-run each time — and
	 * each run re-armed the 100ms "focus the first element" timer, pulling focus to the close button
	 * after every render. A ticking countdown therefore made the voter-verification OTP box
	 * untypeable. The effect is keyed on `openModal` alone and reads the handler through this ref.
	 */
	const handleKeyDownRef = useRef(handleKeyDown);
	useEffect(() => {
		handleKeyDownRef.current = handleKeyDown;
	}, [handleKeyDown]);

	// Same reason as above: read at open time, never a dependency of the open effect.
	const initialFocusRef = useRef(initialFocus);
	useEffect(() => {
		initialFocusRef.current = initialFocus;
	}, [initialFocus]);

	// Store focus and manage focus trap — once per open, never per render.
	useEffect(() => {
		if (openModal) {
			// Store the currently focused element
			previousFocusRef.current = document.activeElement as HTMLElement;

			// Add keyboard listener
			const onKeyDown = (event: KeyboardEvent) => handleKeyDownRef.current(event);
			document.addEventListener('keydown', onKeyDown);

			// Focus the first focusable element in the modal after a short delay
			const timer = setTimeout(() => {
				const preferred = initialFocusRef.current?.();
				if (preferred) {
					preferred.focus();
					return;
				}
				if (modalRef.current) {
					const focusableElements = getFocusableElements(modalRef.current);
					if (focusableElements.length > 0) {
						focusableElements[0].focus();
					}
				}
			}, 100);

			// Prevent body scroll when modal is open
			document.body.style.overflow = 'hidden';

			return () => {
				document.removeEventListener('keydown', onKeyDown);
				clearTimeout(timer);
				document.body.style.overflow = '';
			};
		}
		// Restore focus when modal closes
		if (previousFocusRef.current) {
			previousFocusRef.current.focus();
		}
		return undefined;
	}, [openModal]);

	// Handle close button click
	const onCloseClick = useCallback(() => {
		handleCloseModal();
	}, [handleCloseModal]);

	return ReactDOM.createPortal(
		<Modal
			tabIndex={-1}
			show={openModal}
			className={size === 'wide' ? 'wide-modal' : size === 'medium' ? 'medium-modal' : ''}
			onHide={disableClose ? undefined : handleCloseModal}
			aria-modal='true'
			aria-labelledby={titleId}
			aria-describedby={descriptionId}
		>
			<div
				ref={modalRef}
				role='dialog'
				aria-modal='true'
			>
				<Modal.Dialog
					className={`mx-auto border-0 ${dialogClassName ?? ''}`}
					style={{
						width: '90%',
						background: 'none',
						opacity: opacityNumber ?? 1,
						...dialogStyle,
					}}
				>
					<Modal.Header className='d-flex justify-content-center'>
						<span className='position-relative w-100 text-center'>
							<h5
								id={titleId}
								className='modal-title text-center'
								style={{ fontSize: 'var(--font-size-base)', fontFamily: 'var(--Inter-medium)' }}
							>
								{title}
							</h5>
							{!disableClose && (
								<button
									type='button'
									className='position-absolute d-flex align-items-center justify-content-center'
									data-dismiss='modal'
									style={{
										top: 0,
										right: 0,
										height: '100%',
										cursor: 'pointer',
										background: 'transparent',
										border: 'none',
										padding: '0.5rem',
									}}
									id='closeBtnId'
									onClick={onCloseClick}
									ref={closeRef as React.RefObject<HTMLButtonElement>}
									aria-label='Close modal'
								>
									<CloseSVG aria-hidden='true' />
								</button>
							)}
						</span>
					</Modal.Header>
					<Modal.Body
						style={{ overflow: 'auto', ...bodyStyle }}
						className={`px-4 pt-4 ${bodyClassName ?? ''}`}
					>
						{matchChild}
					</Modal.Body>
				</Modal.Dialog>
			</div>
		</Modal>,
		document.getElementById('root-modal')!
	);
};

export default AppModal;
